/**
 * ContractsDataTable - Contracts table using the reusable DataTable component
 * Migrated from inline MUI Table in Contracts.tsx to v2 DataTable
 */

// react
import React, { useCallback, useContext, useMemo } from 'react';

// router
import { useNavigate } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	alpha,
	Avatar,
	Box,
	Chip,
	LinearProgress,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	AccountTree as IconActivity,
	Assignment as IconWorkPlan,
	Business as IconBusiness,
	CheckCircle as IconCheckCircle,
	DeleteForever as IconDelete,
	Description as IconContract,
	Edit as IconEdit,
	EditNote as IconEditNote,
	Event as IconEvent,
	HourglassEmpty as IconPending,
	Link as IconLink,
	Payment as IconPayment,
	PriorityHigh as IconWarning,
	Verified as IconVerified,
	Visibility as IconView,
} from '@mui/icons-material';

// context
import { ContextProject } from '../../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// components
import {
	ColumnDef,
	DataTable,
	FilterConfig,
	RowAction,
	SortDirection,
} from '../../../ui/DataTable/DataTable';
import { TruncatedText } from '../../../ui/TruncatedText/TruncatedText';

// types
import { V2Contract } from '../../../../@types';

// data
import { COLORS, GRADIENTS } from '../../../../data';

// utils
import { formatCurrency } from '../../../../utils/currency';
import { formatDate } from '../../../../utils/dates';
import { getProjectReportingCurrency, resolveContractFxRate } from '../../../../utils/fxRate';
import {
	getContractAmountPaidReporting,
	getContractAmountPaidV1,
	getContractCommitmentAmount,
	getContractNum,
	getContractPaymentProgressPct,
	getContractSupplierName,
} from './Contracts.utils';

// Types

interface ContractsDataTableProps {
	/** Whether data is loading */
	isLoading?: boolean;
	/** Current search value */
	searchValue?: string;
	/** Search change handler */
	onSearchChange?: (value: string) => void;
	/** Current sort column */
	orderBy?: string;
	/** Current sort direction */
	order?: SortDirection;
	/** Sort change handler */
	onSortChange?: (orderBy: string, order: SortDirection) => void;
	/** Current page (1-indexed) */
	page?: number;
	/** Rows per page */
	rowsPerPage?: number;
	/** Total count for pagination */
	totalCount?: number;
	/** Page change handler */
	onPageChange?: (page: number) => void;
	/** Rows per page change handler */
	onRowsPerPageChange?: (rowsPerPage: number) => void;
	/** Current filter values */
	filterValues?: Record<string, string | number>;
	/** Filter change handler */
	onFilterChange?: (key: string, value: string | number) => void;
	/** Row action: Prepare Payment */
	onPreparePayment?: (contract: V2Contract) => void;
	/** Row action: Edit Contract */
	onEditContract?: (contract: V2Contract) => void;
	/** Row action: Deactivate Contract (soft-delete, non-DRAFT) */
	onDeleteContract?: (contract: V2Contract) => void;
	/** Row action: Permanently delete a DRAFT contract that has no dependents */
	onDeleteDraftContract?: (contract: V2Contract) => void;
	/** Row action: Link an Unassigned contract to an activity */
	onLinkContract?: (contract: V2Contract) => void;
}

// Helpers

/** Get status config for display */
const getStatusConfig = (status: string, t: (key: string) => string) => {
	switch (status) {
		case 'DRAFT':
			return { color: '#6b7280', icon: IconPending, label: t('spendPlan.contracts.statusDraft') };
		case 'SUBMITTED':
			return {
				color: COLORS.warning.main,
				icon: IconPending,
				label: t('spendPlan.contracts.statusSubmitted'),
			};
		case 'ACTIVE':
			return {
				color: COLORS.success.main,
				icon: IconCheckCircle,
				label: t('spendPlan.contracts.statusActive'),
			};
		case 'COMPLETED':
			return {
				color: COLORS.info.main,
				icon: IconVerified,
				label: t('spendPlan.contracts.statusCompleted'),
			};
		case 'CANCELLED':
			return {
				color: COLORS.error.main,
				icon: IconWarning,
				label: t('spendPlan.contracts.statusCancelled'),
			};
		case 'DEACTIVATED':
			return {
				color: COLORS.error.main,
				icon: IconWarning,
				label: t('spendPlan.contracts.statusDeactivated'),
			};
		default:
			return { color: '#6b7280', icon: IconPending, label: status };
	}
};

/** Format date helper */

// Component

export const ContractsDataTable = ({
	isLoading = false,
	searchValue = '',
	onSearchChange,
	orderBy = 'created',
	order = 'DESC',
	onSortChange,
	page = 1,
	rowsPerPage = 10,
	totalCount = 0,
	onPageChange,
	onRowsPerPageChange,
	filterValues = {},
	onFilterChange,
	onPreparePayment,
	onEditContract,
	onDeleteContract,
	onDeleteDraftContract,
	onLinkContract,
}: ContractsDataTableProps) => {
	// Hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const theme = useTheme();

	// Context
	const { project } = useContext(ContextProject);
	const { contracts, workPlans, vouchers } = useContext(ContextSpendPlan);

	// Navigate to contract detail
	const handleRowClick = useCallback(
		(contract: V2Contract) => {
			navigate(`/project/${project?.idWbg}/spend-plans/contract/${contract.id}`);
		},
		[navigate, project]
	);

	// Count of GENERATED (draft) vouchers per contract. Surfacing this lets
	// the PIU find contracts where the supplier (or they themselves) have
	// saved partial payment data and can resume from where it was left.
	const getDraftCount = useCallback(
		(contract: V2Contract) =>
			vouchers.filter((v) => {
				const voucherContractId = typeof v.contract === 'number' ? v.contract : v.contract?.id;
				return voucherContractId === contract.id && v.status === 'GENERATED';
			}).length,
		[vouchers]
	);

	// Get work plan for a contract
	const getWorkPlan = useCallback(
		(contract: V2Contract) => {
			return workPlans.find((wp) => wp.id === contract.workPlan);
		},
		[workPlans]
	);

	// Reporting currency for a contract's headline figures. The commitment,
	// paid and remaining amounts rendered here are all denominated in the
	// reporting currency (the work plan's currency by convention). When a
	// contract is unassigned it has no work plan, so fall back to the
	// project's reporting currency rather than the contract's first amount
	// row — that row carries the local currency and would mislabel a USD
	// figure as the local one (e.g. showing R$ against a USD commitment).
	const getWorkPlanCurrency = useCallback(
		(contract: V2Contract): string => {
			const workPlan = getWorkPlan(contract);
			return workPlan?.currency || getProjectReportingCurrency(project) || 'USD';
		},
		[getWorkPlan, project]
	);

	// Restate a contract's reporting-currency figure into the local currency.
	// Rate resolution prefers, in order: (1) the contract's own captured
	// commitment scalars, (2) its multi-currency amount rows (e.g. an ETB row
	// alongside a USD row implies the true contract rate), (3) the work plan's
	// budget rate as a last resort. The amount-row fallback matters because
	// a contract's amounts can carry a different rate than the work plan it
	// belongs to — using the work-plan rate in that case understates or
	// overstates the local equivalent by the ratio between the two rates.
	const getLocalEquivalent = useCallback(
		(contract: V2Contract, reportingValue: number): string | null => {
			const workPlan = getWorkPlan(contract);
			const fx = resolveContractFxRate(contract, workPlan, project);
			if (!fx) return null;
			return formatCurrency({ currency: fx.localCurrency, value: reportingValue * fx.rate });
		},
		[getWorkPlan, project]
	);

	// Column Definitions

	const columns: ColumnDef<V2Contract>[] = useMemo(
		() => [
			{
				id: 'contractNum',
				label: t('contractDetails'),
				sortKey: 'contractNum',
				minWidth: 220,
				sortable: true,
				render: (contract) => {
					const supplierName = getContractSupplierName(contract);
					return (
						<Stack direction="row" alignItems="flex-start" spacing={1.5}>
							<Avatar
								sx={{
									bgcolor: alpha(GRADIENTS.contract.icon, 0.12),
									color: GRADIENTS.contract.icon,
									height: 28,
									width: 28,
								}}>
								<IconContract sx={{ fontSize: 16 }} />
							</Avatar>
							<Box sx={{ minWidth: 0, maxWidth: 240 }}>
								<TruncatedText
									text={getContractNum(contract)}
									variant="body2"
									sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.25 }}
								/>
								<Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
									<IconBusiness sx={{ color: 'text.secondary', fontSize: 14, flexShrink: 0 }} />
									<TruncatedText
										text={supplierName}
										variant="body2"
										color="text.secondary"
										sx={{ lineHeight: 1.2 }}
									/>
								</Stack>
								{contract.dateSigning && (
									<Stack direction="row" alignItems="center" spacing={0.5}>
										<IconEvent sx={{ color: 'text.disabled', fontSize: 14 }} />
										<Typography variant="caption" color="text.disabled">
											Signed {formatDate({ date: contract.dateSigning })}
										</Typography>
									</Stack>
								)}
							</Box>
						</Stack>
					);
				},
			},
			{
				id: 'activityName',
				label: t('spendPlan.contracts.linkedTo'),
				sortKey: 'activityName',
				minWidth: 160,
				sortable: true,
				render: (contract) => {
					// Story #112: contracts created without a planning_activity_id are
					// surfaced as "Unassigned" with a quick CTA to link them.
					if (!contract.planningActivity) {
						return (
							<Stack direction="row" alignItems="center" spacing={0.75}>
								<Chip
									label={t('spendPlan.contracts.unassigned')}
									size="small"
									variant="outlined"
									sx={{
										borderColor: COLORS.warning.main,
										color: COLORS.warning.main,
										fontWeight: 600,
									}}
								/>
								{onLinkContract && (
									<Tooltip title="Link to activity">
										<Chip
											icon={<IconLink sx={{ fontSize: 14 }} />}
											label="Link"
											size="small"
											clickable
											onClick={(e) => {
												e.stopPropagation();
												onLinkContract(contract);
											}}
											sx={{ fontWeight: 500 }}
										/>
									</Tooltip>
								)}
							</Stack>
						);
					}
					return (
						<Stack spacing={0.75} sx={{ minWidth: 0, maxWidth: 180 }}>
							<Stack direction="row" alignItems="center" spacing={0.5}>
								<IconWorkPlan sx={{ color: COLORS.warning.main, fontSize: 14, flexShrink: 0 }} />
								<TruncatedText
									text={contract.workPlanName || ''}
									variant="body2"
									color="text.secondary"
									sx={{ fontWeight: 500 }}
								/>
							</Stack>
							<Stack direction="row" alignItems="center" spacing={0.5}>
								<IconActivity sx={{ color: COLORS.info.main, fontSize: 14, flexShrink: 0 }} />
								<TruncatedText
									text={contract.activityName || ''}
									variant="body2"
									sx={{ fontWeight: 500 }}
								/>
							</Stack>
						</Stack>
					);
				},
			},
			{
				id: 'amount',
				label: t('contractValue'),
				minWidth: 140,
				align: 'right',
				sortable: false, // amounts is a JSON array, no scalar column to sort
				render: (contract) => {
					const workPlan = getWorkPlan(contract);
					const currency = getWorkPlanCurrency(contract);
					const commitmentAmount = getContractCommitmentAmount(contract, workPlan);
					const commitmentLocal =
						commitmentAmount != null ? getLocalEquivalent(contract, commitmentAmount) : null;

					// One figure per cell: the reporting amount with its local
					// equivalent directly beneath. Remaining lives in its own column
					// so a single cell never stacks more than one pair of numbers.
					return (
						<Box sx={{ textAlign: 'right' }}>
							<Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.4 }}>
								{commitmentAmount != null
									? formatCurrency({ currency, value: commitmentAmount })
									: '—'}
							</Typography>
							{commitmentLocal && (
								<Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.4 }}>
									{commitmentLocal}
								</Typography>
							)}
						</Box>
					);
				},
			},
			{
				id: 'remaining',
				label: t('remaining'),
				minWidth: 140,
				align: 'right',
				sortable: false,
				render: (contract) => {
					const workPlan = getWorkPlan(contract);
					const currency = getWorkPlanCurrency(contract);
					const commitmentAmount = getContractCommitmentAmount(contract, workPlan);
					const paidAmount = getContractAmountPaidReporting(contract);
					const paidV1Amount = getContractAmountPaidV1(contract);
					const remainingAmount =
						commitmentAmount != null ? commitmentAmount - paidAmount - paidV1Amount : null;
					const remainingLocal =
						remainingAmount != null ? getLocalEquivalent(contract, remainingAmount) : null;

					if (remainingAmount == null) {
						return (
							<Typography
								variant="body2"
								sx={{ color: '#9ca3af', textAlign: 'right', display: 'block' }}>
								—
							</Typography>
						);
					}
					return (
						<Box sx={{ textAlign: 'right' }}>
							<Typography
								variant="body2"
								sx={{ fontWeight: 600, color: '#4b5563', lineHeight: 1.4 }}>
								{formatCurrency({ currency, value: remainingAmount })}
							</Typography>
							{remainingLocal && (
								<Typography variant="body2" sx={{ color: '#9ca3af', lineHeight: 1.4 }}>
									{remainingLocal}
								</Typography>
							)}
						</Box>
					);
				},
			},
			{
				id: 'totalPaid',
				label: t('spendPlan.contracts.paymentProgress'),
				sortKey: 'totalPaid',
				minWidth: 200,
				sortable: true,
				render: (contract) => {
					const currency = getWorkPlanCurrency(contract);
					const paidAmount = getContractAmountPaidReporting(contract);
					const paidV1Amount = getContractAmountPaidV1(contract);
					const progressPct = getContractPaymentProgressPct(contract);
					const paidLocal = getLocalEquivalent(contract, paidAmount);
					const paidV1Local = getLocalEquivalent(contract, paidV1Amount);

					return (
						<Stack spacing={0.75}>
							<Stack direction="row" justifyContent="space-between" alignItems="center">
								<Stack spacing={0}>
									<Typography variant="body2" color="text.secondary">
										{formatCurrency({ currency, value: paidAmount })}{' '}
										{t('spendPlan.contracts.paidLabel')}
									</Typography>
									{paidLocal && (
										<Typography variant="body2" sx={{ color: '#6b7280' }}>
											{paidLocal}
										</Typography>
									)}
								</Stack>
								<Typography
									variant="body2"
									sx={{
										fontWeight: 700,
										color:
											progressPct >= 100
												? COLORS.success.main
												: progressPct >= 50
													? COLORS.warning.main
													: 'text.primary',
									}}>
									{progressPct}%
								</Typography>
							</Stack>
							{paidV1Amount > 0 && (
								<Box>
									<Typography variant="caption" color="text.secondary">
										{formatCurrency({ currency, value: paidV1Amount })}{' '}
										{t('spendPlan.contracts.legacyPaid')}
									</Typography>
									{paidV1Local && (
										<Typography
											variant="caption"
											sx={{ display: 'block', color: '#9ca3af', lineHeight: 1.4 }}>
											{paidV1Local}
										</Typography>
									)}
								</Box>
							)}
							<LinearProgress
								variant="determinate"
								value={Math.min(progressPct, 100)}
								sx={{
									bgcolor: alpha(COLORS.success.main, 0.12),
									borderRadius: 1,
									height: 8,
									'& .MuiLinearProgress-bar': {
										bgcolor:
											progressPct >= 100
												? COLORS.success.main
												: progressPct >= 50
													? COLORS.warning.main
													: COLORS.primary.main,
										borderRadius: 1,
									},
								}}
							/>
						</Stack>
					);
				},
			},
			{
				id: 'milestonesCompleted',
				label: t('spendPlan.contracts.milestones'),
				sortKey: 'milestonesCompleted',
				minWidth: 120,
				align: 'center',
				sortable: true,
				render: (contract) => (
					<Tooltip
						title={`${contract.milestonesCompleted} of ${contract.totalPaymentMilestones} payment milestones completed`}>
						<Box>
							<Stack direction="row" justifyContent="center" spacing={0.5} sx={{ mb: 0.5 }}>
								{contract.totalPaymentMilestones > 0 ? (
									Array.from({
										length: Math.min(contract.totalPaymentMilestones, 5),
									}).map((_, idx) => (
										<Box
											key={idx}
											sx={{
												bgcolor:
													idx < contract.milestonesCompleted
														? COLORS.success.main
														: alpha(theme.palette.grey[400], 0.3),
												borderRadius: '50%',
												height: 8,
												width: 8,
											}}
										/>
									))
								) : (
									<Typography variant="caption" color="text.disabled">
										—
									</Typography>
								)}
								{contract.totalPaymentMilestones > 5 && (
									<Typography variant="caption" color="text.secondary">
										+{contract.totalPaymentMilestones - 5}
									</Typography>
								)}
							</Stack>
							<Typography variant="caption" color="text.secondary">
								{contract.milestonesCompleted}/{contract.totalPaymentMilestones}
							</Typography>
						</Box>
					</Tooltip>
				),
			},
			{
				id: 'status',
				label: t('status'),
				sortKey: 'status',
				minWidth: 100,
				align: 'center',
				sortable: true,
				render: (contract) => {
					const statusConfig = getStatusConfig(contract.status, t);
					const draftCount = getDraftCount(contract);
					const draftLabel =
						draftCount === 1
							? t('draftsInProgress', '1 draft in progress', { count: draftCount })
							: t('draftsInProgress', '{{count}} drafts in progress', {
									count: draftCount,
								}).replace('{{count}}', String(draftCount));
					return (
						<Stack alignItems="center" spacing={0.5}>
							<Chip
								icon={<statusConfig.icon sx={{ fontSize: '14px !important', color: 'inherit' }} />}
								label={statusConfig.label}
								size="small"
								sx={{
									bgcolor: alpha(statusConfig.color, 0.1),
									color: statusConfig.color,
									fontWeight: 600,
									'& .MuiChip-icon': {
										color: 'inherit',
									},
								}}
							/>
							{draftCount > 0 && (
								<Tooltip title={t('continueDraft', 'Continue Draft')}>
									<Chip
										icon={<IconEditNote sx={{ fontSize: 14 }} />}
										label={draftLabel}
										size="small"
										variant="outlined"
										color="info"
										clickable
										onClick={(e) => {
											e.stopPropagation();
											onPreparePayment?.(contract);
										}}
										sx={{ fontWeight: 500, height: 22 }}
									/>
								</Tooltip>
							)}
						</Stack>
					);
				},
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps -- 't' provides translations consumed inside columns
		[
			t,
			getWorkPlan,
			getWorkPlanCurrency,
			getLocalEquivalent,
			theme,
			getDraftCount,
			onPreparePayment,
		]
	);

	// Row Actions

	const rowActions: RowAction<V2Contract>[] = useMemo(
		() => [
			// When drafts exist for a contract, relabel the action so the PIU
			// understands they are resuming saved work rather than starting fresh.
			// The underlying handler is identical — the Prepare-for-Payment dialog
			// hydrates from any existing GENERATED vouchers on open.
			{
				label: t('spendPlan.contracts.preparePayment'),
				icon: <IconPayment sx={{ fontSize: 18 }} />,
				onClick: (contract) => onPreparePayment?.(contract),
				show: (contract) => getDraftCount(contract) === 0,
			},
			{
				label: t('continueDraft', 'Continue Draft'),
				icon: <IconPayment sx={{ fontSize: 18 }} />,
				onClick: (contract) => onPreparePayment?.(contract),
				show: (contract) => getDraftCount(contract) > 0,
			},
			{
				label: 'Edit Contract',
				icon: <IconEdit sx={{ fontSize: 18 }} />,
				onClick: (contract) => onEditContract?.(contract),
			},
			{
				label: 'View Details',
				icon: <IconView sx={{ fontSize: 18 }} />,
				onClick: (contract) => handleRowClick(contract),
			},
			{
				label: 'Delete Draft',
				icon: <IconDelete sx={{ fontSize: 18 }} />,
				onClick: (contract) => onDeleteDraftContract?.(contract),
				show: (contract) => contract.status === 'DRAFT' && Boolean(onDeleteDraftContract),
				destructive: true,
				dividerBefore: true,
			},
			{
				label: 'Deactivate Contract',
				onClick: (contract) => onDeleteContract?.(contract),
				show: (contract) => contract.status !== 'DRAFT',
				destructive: true,
				dividerBefore: true,
			},
		],
		[
			t,
			onPreparePayment,
			onEditContract,
			onDeleteContract,
			onDeleteDraftContract,
			handleRowClick,
			getDraftCount,
		]
	);

	// Filters

	const filters: FilterConfig[] = useMemo(
		() => [
			{
				key: 'status',
				label: t('status'),
				options: [
					{ label: 'All Status', value: 'ALL' },
					{ label: 'Draft', value: 'DRAFT' },
					{ label: 'Submitted', value: 'SUBMITTED' },
					{ label: 'Active', value: 'ACTIVE' },
					{ label: 'Completed', value: 'COMPLETED' },
					{ label: 'Cancelled', value: 'CANCELLED' },
					{ label: 'Deactivated', value: 'DEACTIVATED' },
				],
			},
			{
				key: 'workPlan',
				label: 'Work Plan',
				options: [
					{ label: 'All Work Plans', value: 'ALL' },
					...workPlans.map((wp) => ({ label: wp.name, value: wp.id })),
				],
			},
		],
		[workPlans]
	);

	// Render

	return (
		<DataTable<V2Contract>
			data={contracts}
			columns={columns}
			getRowKey={(contract) => contract.id}
			loading={isLoading}
			emptyMessage={t('empty.contracts.title')}
			emptyContent={
				searchValue || filterValues.workPlan || filterValues.status
					? t('spendPlan.tryAdjustingFilters')
					: t('empty.contracts.content')
			}
			// Sorting
			orderBy={orderBy}
			order={order}
			onSortChange={onSortChange}
			// Pagination
			page={page}
			rowsPerPage={rowsPerPage}
			totalCount={totalCount}
			onPageChange={onPageChange}
			onRowsPerPageChange={onRowsPerPageChange}
			// Search & Filters
			searchPlaceholder={t('spendPlan.contracts.searchPlaceholder')}
			searchValue={searchValue}
			onSearchChange={onSearchChange}
			showFilters
			filters={filters}
			filterValues={filterValues}
			onFilterChange={onFilterChange}
			// Row Actions
			rowActions={rowActions}
			onRowClick={handleRowClick}
		/>
	);
};
